'use client';

import React, { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { usePathname, useSearchParams } from 'next/navigation';

import Button from '@/components/ui/button/button';
import InputField from '@/components/ui/input-field/input-field';
import { useUser } from '@/providers/UserProvider';
import { useTenantConfig } from '@/providers/ConfigProvider';
import useGetMobileAppWebViewHeader from '@/hooks/use-get-mobile-app-web-view';
import { initiateLogin } from '@/helpers/api/user-profile-api';
import {
    createLpoCheckout,
    LpoCheckoutError,
} from '@/helpers/api/lpo-checkout-api';
import {
    isPurchasable,
    MAX_QUANTITY,
    validateQuantity,
} from '@/lib/stripe/price';

export interface BuyNowOffer {
    sys: { id: string };
    slug: string;
    name: string;
    salesPrice?: string | null;
    offerValidTill?: string | null;
}

export interface BuyNowLabels {
    buyNow?: string;
    processing?: string;
    loginToBuy?: string;
    quantity?: string;
    error?: string;
}

interface BuyNowProps {
    offer: BuyNowOffer;
    nodeLocale: string;
    parentSlug: string;
    stripeEnabled: boolean;
    mpId?: string;
    labels?: BuyNowLabels;
}

const DEFAULT_LABELS: Required<BuyNowLabels> = {
    buyNow: 'Buy now',
    processing: 'Processing…',
    loginToBuy: 'Log in to buy',
    quantity: 'Quantity',
    error: 'Something went wrong. Please try again.',
};

export const BuyNow = ({
    offer,
    nodeLocale,
    parentSlug,
    stripeEnabled,
    mpId,
    labels,
}: BuyNowProps) => {
    const { isLoggedIn } = useUser();
    const config = useTenantConfig();
    const isWebView = useGetMobileAppWebViewHeader();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [quantity, setQuantity] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    const text = { ...DEFAULT_LABELS, ...labels };
    const purchasable = useMemo(
        () => isPurchasable(offer, DateTime.now()),
        [offer],
    );

    if (!stripeEnabled || !purchasable) return null;

    const handleQuantityChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        const parsed = parseInt(event.target.value, 10);
        if (Number.isNaN(parsed)) {
            setQuantity(1);
            return;
        }
        setQuantity(Math.min(Math.max(parsed, 1), MAX_QUANTITY));
    };

    const getCurrentPath = (): string => {
        const params = searchParams.toString();
        return params ? `${pathname}?${params}` : (pathname ?? '/');
    };

    const handleLogin = async () => {
        const brandId =
            config?.MEETINGPLACE_TAG_CONTENTFUL_ID ||
            config?.NEXT_PUBLIC_MEETINGPLACE_TAG_CONTENTFUL_ID ||
            '';
        try {
            await initiateLogin(nodeLocale, brandId, config, getCurrentPath());
        } catch (error) {
            console.error('[BuyNow] login initiation failed', error);
        }
    };

    const handleBuy = async () => {
        if (loading || !validateQuantity(quantity)) return;
        setLoading(true);
        setHasError(false);
        try {
            const { url } = await createLpoCheckout({
                offerId: offer.sys.id,
                quantity,
                locale: nodeLocale,
                parentSlug,
                prefix: config?.GATSBY_SITE_PATH_PREFIX || '',
                webView: isWebView,
                mpid: mpId,
            });
            window.location.href = url;
        } catch (error) {
            // Session expired mid-flow → send the user to log in again.
            if (error instanceof LpoCheckoutError && error.status === 401) {
                setLoading(false);
                await handleLogin();
                return;
            }
            setHasError(true);
            setLoading(false);
        }
    };

    if (!isLoggedIn) {
        return (
            <div className="mt-4">
                <Button className="w-full text-center" onClick={handleLogin}>
                    {text.loginToBuy}
                </Button>
            </div>
        );
    }

    return (
        <div className="mt-4 flex flex-col gap-3">
            <div className="w-28">
                <InputField
                    name="lpo-quantity"
                    id="lpo-quantity"
                    label={text.quantity}
                    type="number"
                    min={1}
                    max={MAX_QUANTITY}
                    step={1}
                    value={quantity}
                    onChange={handleQuantityChange}
                    disabled={loading}
                />
            </div>
            <Button
                className="w-full text-center"
                disabled={loading}
                onClick={handleBuy}
            >
                {loading ? text.processing : text.buyNow}
            </Button>
            {hasError && (
                <p className="text-bodys text-semantic-negative" role="alert">
                    {text.error}
                </p>
            )}
        </div>
    );
};

export default BuyNow;
